import axios from 'axios';
import { authService } from './auth';

// Use relative path so the app works on any domain (same-origin API proxy)
// In development, set VITE_API_URL in .env.local to http://localhost:8082/api/v1
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

class ApiService {
  private client: any;
  private authToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor: Add auth token
    // NOTE: use getAuthToken() (not this.authToken directly) so that a token
    // stored in localStorage by a previous visit is picked up automatically on
    // fresh page loads (e.g. after the Facebook OAuth redirect).
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Add business ID header (required by backend JWTAuth middleware)
        // when token doesn't have embedded business_id claim
        const businessId = authService.getBusinessId();
        if (businessId) {
          config.headers['X-Business-ID'] = businessId;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: Handle errors
    this.client.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const status = error.response?.status;
        const config = error.config as any;

        if (status === 401 && config && !config._retry) {
          config._retry = true;
          const isIframe = window !== window.parent;

          if (isIframe) {
            window.parent.postMessage({ type: 'BIZTADA_REQUIRE_AUTH_REFRESH' }, '*');
            
            try {
              const newToken = await authService.waitForNewToken(10000); // Wait up to 10s
              if (newToken) {
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${newToken}`;
                const retryResponse = await this.client.request(config);
                return retryResponse; // client.request returns the response directly in our wrapper? Wait, the wrapper uses response.data above. I'll return retryResponse.
              }
            } catch (e) {
              console.error('Failed to wait for new token', e);
            }
          }

          // Unauthorized - clear token.
          // Exception: don't redirect during FB OAuth callback — it handles errors itself.
          // Exception: DEV mode with VITE_DEV_TOKEN — redirect would cause infinite loop.
          this.clearAuthToken();
          const isDevBypass = import.meta.env.DEV && import.meta.env.VITE_DEV_TOKEN;
          if (!isDevBypass && !isIframe && !window.location.pathname.includes('/auth/facebook/callback')) {
            window.location.href = '/';
          }
        } else if (status === 400) {
          const errorCode = error.response.data?.code;
          if (errorCode === 'INVALID_CONTEXT' || errorCode === 'BUSINESS_ID_REQUIRED') {
            // Missing business context from CRM
            alert('Please select a business first');
            // If running as module in biztada-app, notify parent
            window.parent?.postMessage({ type: 'BUSINESS_REQUIRED' }, '*');
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token: string) {
    this.authToken = token;
    localStorage.setItem('session_token', token);
  }

  getAuthToken(): string | null {
    if (!this.authToken) {
      this.authToken = localStorage.getItem('session_token');
    }
    return this.authToken;
  }

  clearAuthToken() {
    this.authToken = null;
    localStorage.removeItem('session_token');
  }

  // Generic HTTP methods
  async get(url: string, config?: any) {
    return this.client.get(url, config);
  }

  async post(url: string, data?: any, config?: any) {
    return this.client.post(url, data, config);
  }

  async put(url: string, data?: any, config?: any) {
    return this.client.put(url, data, config);
  }

  async patch(url: string, data?: any, config?: any) {
    return this.client.patch(url, data, config);
  }

  async delete(url: string, config?: any) {
    return this.client.delete(url, config);
  }

  // Chatbot-specific endpoints

  // Platforms
  async getAvailablePlatforms() {
    return this.get('/platforms/available');
  }

  async enableChatbotPlatform(id: string) {
    return this.post('/platforms/enable', { page_id: id });
  }

  async updatePlatform(id: string, data: any) {
    return this.put(`/platforms/${id}`, data);
  }

  async deletePlatform(id: string) {
    return this.delete(`/platforms/${id}`);
  }

  // Browser Accounts
  async updateBrowserAccount(id: string, data: any) {
    return this.put(`/browser-accounts/${id}`, data);
  }

  // Conversations
  async getConversation(id: string) {
    return this.get(`/conversations/${id}`);
  }

  // Messages
  async getMessages(conversationId: string, params?: any) {
    return this.get(`/conversations/${conversationId}/messages`, { params });
  }

  async sendMessage(conversationId: string, content: string, contentType: string = 'text') {
    return this.post(`/conversations/${conversationId}/messages`, {
      content,
      content_type: contentType,
    });
  }

  // Scenarios
  async getScenarios() {
    return this.get('/scenarios');
  }

  // Worker status overview (mirrors svc-business-marketing /my-workers)
  // Response: { workers: WorkerSummary[], total_platforms: number }
  async getMyChatbots() {
    return this.get('/my-chatbots');
  }

  // Idempotent: returns existing worker if already assigned, allocates a new one otherwise.
  // Response: { success: true, worker_uuid: string, is_new: boolean, message: string }
  // 503 when no worker available: { error: 'NO_WORKER_AVAILABLE', message: string }
  async requestWorkerAllocation() {
    return this.post('/worker/allocate');
  }


  // Get worker tunnel URL + short-lived JWT for direct frontend→worker calls.
  // Mirrors svc-business-marketing's getWorkerReportURL / getWorkerScheduleURL pattern.
  //
  // Response: {
  //   success: boolean,
  //   worker: { uuid: string, url: string, status: string, mode: string },
  //   jwt_token: string,   // 1-hour JWT signed with JWT_SECRET_KEY
  //   endpoints: { test_chat, agent_send, agent_send_image, agent_control, ... }
  // }
  //
  // The jwt_token is accepted by wkr-chatbot's verify_jwt_token dependency on all
  // direct-call routes (test-chat, agent-*, voice, onboarding, knowledge-base, etc.).
  async getWorkerURL(): Promise<WorkerURLInfo> {
    return this.get('/worker/url');
  }
}

export interface WorkerURLInfo {
  success: boolean;
  worker: {
    uuid: string;
    url: string;
    status: string;
    mode: string;
  };
  jwt_token: string;
  endpoints: {
    test_chat: string;
    test_chat_history: string;
    agent_send: string;
    agent_send_image: string;
    agent_control: string;
    onboarding: string;
    bot_config: string;
    voice_transcribe: string;
    voice_tts: string;
    kb_search: string;
    kb_extract_ai: string;
    kb_interview: string;
  };
}

export const apiService = new ApiService();
